import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMfa } from "@/hooks/use-mfa";
import { useAppStore } from "@/store";
import { useApiClient } from "@/lib/auth";
import { type ThemePreference, type LocalePreference } from "@njbsoft/store";
import { PageHeader } from "@/components/ui";
import { IdentitySection } from "@/components/settings/identity-section";
import { AppearanceSection } from "@/components/settings/appearance-section";
import { LanguageSection } from "@/components/settings/language-section";
import { SecuritySection } from "@/components/settings/security-section";

export const Route = createFileRoute("/_authenticated/settings/")({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation("settings");
  const { status } = useMfa();
  const mfaEnabled = (status.data?.methods?.length ?? 0) > 0;
  const themePreference = useAppStore((s) => s.themePreference);
  const setThemePreference = useAppStore((s) => s.setThemePreference);
  const localePreference = useAppStore((s) => s.localePreference);
  const setLocalePreference = useAppStore((s) => s.setLocalePreference);
  const accentColor = useAppStore((s) => s.accentColor);
  const setAccentColor = useAppStore((s) => s.setAccentColor);
  const api = useApiClient();

  const handleThemeChange = (pref: ThemePreference) => {
    setThemePreference(pref);
    api.PATCH("/users/me/preferences", { body: { theme: pref } }).catch(() => {
      // Silently ignore -- local storage is authoritative
    });
  };

  const handleLocaleChange = (pref: LocalePreference) => {
    setLocalePreference(pref);
    api.PATCH("/users/me/preferences", { body: { locale: pref } }).catch(() => {
      // Silently ignore -- local storage is authoritative
    });
  };

  const handleAccentChange = (value: string) => {
    setAccentColor(value);
    api.PATCH("/users/me/preferences", { body: { accent_color: value } }).catch(() => {
      // Silently ignore -- local storage is authoritative
    });
  };

  return (
    <div className="mx-auto max-w-2xl p-8">
      <PageHeader title={t("title")} />

      <IdentitySection />

      <AppearanceSection
        themePreference={themePreference}
        onThemeChange={handleThemeChange}
        accentColor={accentColor}
        onAccentChange={handleAccentChange}
      />

      <LanguageSection localePreference={localePreference} onLocaleChange={handleLocaleChange} />

      <SecuritySection mfaEnabled={mfaEnabled} />
    </div>
  );
}
